import { Injectable, computed, inject, signal } from '@angular/core';
import { StaffRole, StaffUser } from '../models/tournament.models';
import { SupabaseService } from './supabase.service';

/**
 * Staff sign in with a username; Supabase Auth needs an email, so each account
 * is created as `<username>` + this suffix. Must match the users created in
 * Authentication → Users and supabase/manual/01_link_staff_profiles.sql.
 */
const STAFF_EMAIL_SUFFIX = '@ksh.sprentzo.com';

/**
 * STAFF AUTH
 * ----------
 * Sign-in goes through Supabase Auth; the role and sport come from the
 * staff_profiles table. The checks below only decide what the panel shows —
 * Row Level Security in the database enforces the same rules for real.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly db = inject(SupabaseService).db;

  private readonly _currentUser = signal<StaffUser | null>(null);
  readonly currentUser = this._currentUser.asReadonly();
  readonly isLoggedIn = computed(() => this._currentUser() !== null);

  readonly isAdmin = computed(() => this._currentUser()?.role === 'admin');

  /** The admin and the gallery account publish photographs; scorers don't. */
  readonly canManageGallery = computed(() => {
    const role = this._currentUser()?.role;
    return role === 'admin' || role === 'gallery';
  });

  /** Settles once a session saved in this browser has been restored (or found missing). */
  readonly ready: Promise<void> = this.loadProfile();

  constructor() {
    // Supabase warns against awaiting its own calls inside this callback, so only update state here.
    this.db.auth.onAuthStateChange(event => {
      if (event === 'SIGNED_OUT') this._currentUser.set(null);
    });
  }

  async signIn(username: string, password: string): Promise<{ success: boolean; error?: string }> {
    const email = username.trim().toLowerCase() + STAFF_EMAIL_SUFFIX;
    const { error } = await this.db.auth.signInWithPassword({ email, password });

    if (error) {
      // One message for both failures, so the form never confirms which usernames exist.
      const offline = /fetch|network/i.test(error.message);
      return {
        success: false,
        error: offline ? "Couldn't reach the server. Check the connection and try again." : 'Username or password is incorrect.',
      };
    }

    await this.loadProfile();
    if (!this._currentUser()) {
      await this.db.auth.signOut();
      return { success: false, error: 'This login has no staff role yet. Ask the administrator to link it.' };
    }
    return { success: true };
  }

  async signOut(): Promise<void> {
    await this.db.auth.signOut();
    this._currentUser.set(null);
  }

  /**
   * Whether this account may see a sport at all. A scorer gets exactly one;
   * the gallery account gets none — photographs are its only job.
   */
  canAccessSport(sportId: string): boolean {
    const u = this._currentUser();
    if (!u) return false;
    if (u.role === 'admin') return true;
    return u.role === 'scorer' && u.sportId === sportId;
  }

  private async loadProfile(): Promise<void> {
    try {
      const { data: { session } } = await this.db.auth.getSession();
      if (!session) {
        this._currentUser.set(null);
        return;
      }
      const { data, error } = await this.db
        .from('staff_profiles')
        .select('username, role, sport_id')
        .eq('id', session.user.id)
        .maybeSingle();
      if (error) throw error;

      const profile = data as { username: string; role: StaffRole; sport_id: string | null } | null;
      this._currentUser.set(profile
        ? { username: profile.username, role: profile.role, sportId: profile.sport_id }
        : null);
    } catch (err) {
      console.error('Restoring the staff session failed', err);
      this._currentUser.set(null);
    }
  }
}
